import * as signalR from "@microsoft/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import * as Icons from "@mui/icons-material/";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import {
    AppBar,
    Badge,
    Button,
    Grid,
    IconButton,
    ListItem,
    ListItemText,
    MenuItem,
    Popover,
    Snackbar,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@material-ui/core";
import { Theme, createStyles, fade, makeStyles, useTheme } from "@material-ui/core/styles";
import {
    AccountCircle,
    Archive,
    Assessment,
    Ballot,
    Home,
    Notifications,
    PowerSettingsNew,
    Settings,
    SettingsSystemDaydream,
    Translate,
} from "@material-ui/icons";
import MuiAlert, { AlertProps } from "@material-ui/lab/Alert";
import { Menu } from "@mui/material";
import { useSnackbar } from "notistack";
import { Profile } from "oidc-client";
import { useTranslation } from "react-i18next";
import AstraLogo from "../../../../assets/images/ico-ideaastra-400x300px-yellow.png";
import HunosaLogo from "../../../../assets/images/hunosa.png";

import { MarkAsReadCommand, ProjectDto, UserNotificationDto } from "../../../api/APIClient";
import { BDTRoutes } from "../../../helpers/applicationConstants";

import { notificationsService } from "../../../api/Services";

import i18n from "../../../../i18n";
import { ApplicationPaths } from "../../../auth/ApiAuthorizationConstants";
import { authService } from "../../../auth/AuthorizeService";

import CookiesModel from "../../../CookiesModel";
import { FullAssetInformation } from "../../../Models/AssetInformationModels";
import { PanelAppContext } from "../../../PanelAppProvider";
import { usePureEffect } from "../../../helpers/usePureEffect";
import { usePanelProjectContext } from "../../PanelProjectProvider";
import useAsset from "../../Project/Viewer/hooks/useAsset";
import AlertPopover from "./Popovers/AlertPopover/AlertPopover";
import HistoricalAdvertises from "./Popovers/HistoricalAdvertises/HistoricalAdvertises";
import UserSettingsModal from "./UserSettings/UserSettingsModal";

function Alert(props: AlertProps) {
    return <MuiAlert elevation={24} variant="filled" {...props} />;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 2,
            background:
                "linear-gradient(90deg, #3D3D3B 0%, #3D3D3B 40%, #428DC7 55%, #428DC7 100%)",
            boxShadow: `0  -2px 6px 0px ${theme.palette.secondary.main}`,
        },
        toolBar: {
            justifyContent: "space-between",
        },

        menuButton: {
            color: theme.palette.background.default,
        },
        search: {
            position: "relative",
            borderRadius: theme.shape.borderRadius,
            backgroundColor: fade(theme.palette.secondary.light, 0.35),
            "&:hover": {
                backgroundColor: fade(theme.palette.secondary.light, 0.55),
            },
            marginLeft: 0,
            width: "100%",
            [theme.breakpoints.up("sm")]: {
                marginLeft: theme.spacing(1),
                width: "auto",
            },
            [theme.breakpoints.down("xs")]: {
                display: "none",
            },
        },
        searchIcon: {
            padding: theme.spacing(0, 2),
            height: "100%",
            position: "absolute",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        inputRoot: {
            color: "inherit",
        },
        userMenuItemIcon: {
            paddingLeft: 10,
        },
        typography: {
            padding: theme.spacing(2),
        },
        logo: {
            position: "absolute",
            marginLeft: "48%",
        },
        dev: {
            position: "absolute",
            width: "100%",
            height: "80%",
            // backgroundImage: "url(https://cdn-icons-png.flaticon.com/512/126/126428.png)",
            backgroundSize: "contain",
            backgroundRepeat: "space",
            opacity: 0.035,
            zIndex: -1,
        },
        customBadge: {
            backgroundColor: "#851932",
        },
    })
);

const notificationsConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/notifications", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

const qrConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/qr", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

interface IPanelAppProvider {
    cookies: CookiesModel | undefined;
    setCookies: React.Dispatch<React.SetStateAction<CookiesModel | undefined>>;
}

const TopBar = (): JSX.Element => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("main");

    const emptyAlertStyles = { width: "25%", maxWidth: "300px" };
    const alertStyles = { width: "30%", minWidth: "450px" };

    const {
        project,
        notifications,
        setNotifications,
        notificationsCount,
        setNotificationsCount,
        alarmsCount,
        setAlarmsCount,
        isViewerReady,
        setView,
        setAssetSelection,
        setIsRightPanelOpen,
        setIsRenderingItems,
        setUserProfile,
        userId,
        setUserId,
        userProfilePhoto,
        setIsViewerLoading,
        userRole,
        setRedirectToChatThread,
    } = usePanelProjectContext();

    const { cookies, setCookies } = React.useContext<IPanelAppProvider>(PanelAppContext);

    const location = useLocation();
    const classes = useStyles();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
    const [isDev, setIsDev] = useState<boolean>(true);

    const [popUpNotifications, setPopUpNotifications] = useState(false);
    const togglePopupNotifications = () => {
        setPopUpNotifications(!popUpNotifications);
    };
    const [popUpAlarms, setPopUpAlarms] = useState(false);
    const togglePopupAlarms = () => {
        setPopUpAlarms(!popUpAlarms);
    };

    const [snackbarAlarms, setSnackbarAlarms] = React.useState<{
        state: boolean;
        signalId: string;
        description: string;
        date: string;
    }>({
        state: false,
        signalId: "",
        description: "",
        date: "",
    });

    const handleCloseSnackbarAlarms = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackbarAlarms({
            state: false,
            signalId: "",
            description: "",
            date: "",
        });
    };

    const [notificationsHistoryList, setNotificationsHistoryList] = useState<UserNotificationDto[]>(
        []
    );
    const getNotificationsHistoryList = async () => {
        notificationsService
            .getAllNotificationsByUser(project.id)
            .then((res) => {
                setNotificationsHistoryList(res.data);
            })
            .catch((err) => {
                enqueueSnackbar(`${err.errorDescription.message}`, {
                    variant: "error",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                console.error(err);
            });
    };

    const heightCalc = window.innerHeight - 400;

    const [anchorElPopOver, setAnchorElPopOver] = useState<HTMLButtonElement | null>(null);
    const [anchorElPopOverAlarms, setAnchorElPopOverAlarms] = useState<HTMLButtonElement | null>(
        null
    );
    const [profile, setProfile] = useState<Profile | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    useEffect(() => {
        authService.getUser().then((user) => {
            if (user && user.profile) {
                setUserProfile(user?.profile);
                setProfile(user?.profile);
                setUserId(user.profile.oid);
            }
            setUserId(user?.profile.oid as string);
            const validEncoded = user?.access_token.split(".")[1];
            const decondedAccessToken = validEncoded ? atob(validEncoded) : "yay";
            const isAdminX = decondedAccessToken.includes(`"roles":["administrator"]`);
            setIsAdmin(isAdminX);
        });
    }, []);

    const handleClickPopOver = (event: React.MouseEvent<HTMLButtonElement>) => {
        notificationsService.getUnreadNotifications(project.id).then((res) => {
            if (res && res.data) {
                setNotifications(res.data);
                setNotificationsCount(res.data.length);
            } else {
                setNotifications([]);
                setNotificationsCount(0);
            }
        });
        setAnchorElPopOver(event.currentTarget);
    };

    const handleClosePopOver = () => {
        setAnchorElPopOver(null);
    };
    const handleCLosePopOverAlarms = () => {
        setAnchorElPopOverAlarms(null);
    };

    const openPopOver = Boolean(anchorElPopOver);
    const openPopOverAlarms = Boolean(anchorElPopOverAlarms);
    const idPopOver = openPopOver ? "simple-popover" : undefined;
    const idPopOverAlarms = openPopOverAlarms ? "simple-popover" : undefined;

    const [assetIdCommentsSelected, setAssetIdCommentsSelected] = useState<string | undefined>(
        undefined
    );
    const [assetIdSignalsSelected, setAssetIdSignalsSelected] = useState<string | undefined>(
        undefined
    );

    const [dataRigthPanelSelected, setDataRigthPanelSelected] = useState<
        FullAssetInformation | undefined
    >(undefined);

    const { fetchingAssetInformationById } = useAsset();

    const handleCommentsPanelItemClick = (route: string, asset: string, thread: string) => {
        if (!isViewerReady) history.push(route);

        handleClosePopOver();
        setAssetIdCommentsSelected(asset);
        setRedirectToChatThread(thread);
    };

    const handleSignalsPanelItemClick = (route: string, asset: string) => {
        if (!isViewerReady) history.push(route);

        handleCLosePopOverAlarms();
        setAssetIdSignalsSelected(asset);
    };

    React.useEffect(() => {
        if (isViewerReady && assetIdCommentsSelected !== undefined) {
            setAssetIdCommentsSelected(undefined);
            fetchingAssetInformationById(project.id, assetIdCommentsSelected, true, undefined);
            setView("chat");
        }
        if (isViewerReady && assetIdSignalsSelected !== undefined) {
            setAssetIdSignalsSelected(undefined);
            fetchingAssetInformationById(project.id, assetIdSignalsSelected, true, undefined);
            setView("alarms");
        }
    }, [isViewerReady, assetIdCommentsSelected, assetIdSignalsSelected, dataRigthPanelSelected]);

    const subscribeToNotificationsSignalR = async (updatedProjectId: ProjectDto) => {
        authService.getProfile().then((profResponse) => {
            if (profResponse !== null) {
                if (notificationsConnection.state === HubConnectionState.Disconnected)
                    setProfile(profResponse);
                notificationsConnection
                    .start()
                    .then(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null) {
                                notificationsConnection.invoke("AddUserConnection", prof.oid);
                                notificationsConnection.on(
                                    "PushNotifications",
                                    (theNewProjectId) => {
                                        notificationsService
                                            .getUnreadNotificationsCount(theNewProjectId)
                                            .then((res) => {
                                                setNotificationsCount(res.data.number);
                                            })
                                            .catch((err) => {
                                                enqueueSnackbar(`${err.errorDescription.message}`, {
                                                    variant: "error",
                                                    anchorOrigin: {
                                                        horizontal: "right",
                                                        vertical: "bottom",
                                                    },
                                                });
                                                console.error(err);
                                            });
                                    }
                                );
                            }
                        });
                    })
                    .catch((err) => {
                        enqueueSnackbar(`${err}`, {
                            variant: "error",
                            anchorOrigin: {
                                horizontal: "right",
                                vertical: "bottom",
                            },
                        });
                        console.error(err);
                    })
                    .finally(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null) {
                                notificationsService
                                    .getUnreadNotificationsCount(updatedProjectId.id)
                                    .then((res) => {
                                        setNotificationsCount(res.data.number);
                                    })
                                    .catch((err) => {
                                        enqueueSnackbar(`${err}`, {
                                            variant: "error",
                                            anchorOrigin: {
                                                horizontal: "right",
                                                vertical: "bottom",
                                            },
                                        });
                                        console.error(err);
                                    });
                            }
                        });
                    });
            }
        });
    };

    const subscribeToQRSignalR = async () => {
        qrConnection
            .start()
            .then(() => {
                authService.getProfile().then((prof) => {
                    if (prof !== null) {
                        qrConnection.invoke("AddQRConnection", prof.oid);
                        qrConnection.on("SendQR", (qrOutput: string) => {
                            fetchingAssetInformationById(project.id, qrOutput, true, undefined);
                            window.NOP_VIEWER.setGhosting(false);
                        });
                    }
                });
            })
            .catch((err) => {});
    };

    useEffect(() => {
        const dev =
            window.location.host.includes("localhost") || window.location.host.includes("5050");
        setIsDev(dev);
    }, []);

    usePureEffect(() => {
        setView("");
        setAssetSelection({});
        setIsRightPanelOpen(false);
        setIsRenderingItems(false);

        if (notificationsConnection.state === HubConnectionState.Connected) {
            notificationsConnection.stop().then(() => {
                subscribeToNotificationsSignalR(project);
            });
        } else {
            subscribeToNotificationsSignalR(project);
        }

        if (qrConnection.state === HubConnectionState.Connected) {
            qrConnection.stop().then(() => {
                subscribeToQRSignalR();
            });
        } else {
            subscribeToQRSignalR();
        }
    }, [project]);

    const [anchorProfile, setAnchorProfile] = React.useState<null | HTMLElement>(null);
    const handleClickProfile = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorProfile(event.currentTarget);
    };
    const handleCloseProfile = () => {
        setAnchorProfile(null);
    };

    const [anchorSettings, setAnchorSettings] = React.useState<null | HTMLElement>(null);
    const handleClickSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorSettings(event.currentTarget);
    };
    const handleCloseSettings = () => {
        setAnchorSettings(null);
    };

    const handleMenuItemClick = (route: string) => {
        history.push(route);
        handleCloseSettings();
        handleCloseProfile();
    };

    const [anchorLanguages, setAnchorLanguages] = React.useState<null | HTMLElement>(null);
    const handleClickLanguages = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorLanguages(event.currentTarget);
    };
    const handleCloseLanguages = () => {
        setAnchorLanguages(null);
    };

    const handleMenuLanguageClick = (langSelected: string) => {
        i18n.changeLanguage(langSelected);
        // TODO set user language on server
        handleCloseLanguages();
    };

    const handleLogOut = () => {
        authService.getProfile().then((prof) => {
            if (prof !== null) {
                notificationsConnection.invoke("RemoveUserConnection", prof.sub).finally(() => {
                    history.push(ApplicationPaths.LogOut, { local: true });
                    handleCloseSettings();
                    handleCloseProfile();
                });
            }
        });
    };

    const handleReadNotification = async (notId, projectid) => {
        if (notId) {
            const newNotId = notId.split(",").map((x) => Number(x));
            const command: MarkAsReadCommand = { ids: newNotId, projectId: projectid };
            await notificationsService.markAsRead(command);
            notificationsService.getUnreadNotifications(projectid).then((res) => {
                if (res) {
                    setNotifications(res.data);
                    setNotificationsCount(res.data.length);
                } else {
                    setNotifications([]);
                    setNotificationsCount(0);
                }
            });
        }
    };

    const [userSettings, setUserSettings] = useState(false);

    return (
        <div className={classes.root}>
            <AppBar className={classes.appBar} position="fixed" color="secondary">
                <Toolbar className={classes.toolBar}>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            {/* Need this empty space. Fdo.Anita */}
                        </div>
                        {/* Need this empty space. Fdo.Anita */}
                    </div>

                    <div className={classes.logo}>
                        <Link to={BDTRoutes.Home}>
                            <img
                                style={{ marginTop: "6px" }}
                                height="54"
                                src={HunosaLogo}
                                alt="Astra logo"
                            />
                        </Link>
                    </div>

                    {isDev && <div className={classes.dev} />}

                    <div>
                        <Tooltip title={`${t("topbar.home")}`}>
                            <IconButton
                                component={Link}
                                to={BDTRoutes.Home}
                                className={classes.menuButton}
                            >
                                <Home />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={`${t("topbar.translations")}`}>
                            <IconButton
                                onClick={handleClickLanguages}
                                className={classes.menuButton}
                            >
                                <Translate />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorLanguages}
                            open={Boolean(anchorLanguages)}
                            onClose={handleCloseLanguages}
                            autoFocus={false}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            <MenuItem
                                style={{ backgroundColor: i18n.language === "es" ? "#F5F5F5" : "" }}
                                onClick={() => handleMenuLanguageClick("es")}
                            >
                                <Typography variant="body1">{t("languages.spanish")}</Typography>
                            </MenuItem>
                            <MenuItem
                                style={{ backgroundColor: i18n.language === "en" ? "#F5F5F5" : "" }}
                                onClick={() => handleMenuLanguageClick("en")}
                            >
                                <Typography variant="body1">{t("languages.english")}</Typography>
                            </MenuItem>
                            <MenuItem
                                style={{ backgroundColor: i18n.language === "fr" ? "#F5F5F5" : "" }}
                                onClick={() => handleMenuLanguageClick("fr")}
                            >
                                <Typography variant="body1">{t("languages.french")}</Typography>
                            </MenuItem>
                        </Menu>

                        {isAdmin && (
                            <Tooltip title={`${t("topbar.settings")}`}>
                                <IconButton
                                    onClick={handleClickSettings}
                                    className={classes.menuButton}
                                >
                                    <Settings />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Menu
                            anchorEl={anchorSettings}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorSettings)}
                            onClose={handleCloseSettings}
                            autoFocus={false}
                        >
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.forge")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.ForgeAccounts);
                                }}
                            >
                                <SettingsSystemDaydream />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.forgeAccountsAdmin")}
                                </span>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.Models);
                                }}
                            >
                                <Archive />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.modelsAdmin")}
                                </span>
                            </MenuItem>
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.powerBI")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.PowerBIAccounts);
                                }}
                            >
                                <Assessment />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.powerBIAccountsAdmin")}
                                </span>
                            </MenuItem>
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.projects")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.Projects);
                                }}
                            >
                                <Ballot />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.projectsAdmin")}
                                </span>
                            </MenuItem>
                        </Menu>

                        <Tooltip title={`${t("topbar.notifications")}`}>
                            <IconButton
                                onClick={(event) => {
                                    handleClickPopOver(event);
                                }}
                                className={classes.menuButton}
                            >
                                <Badge
                                    badgeContent={notificationsCount}
                                    classes={{ badge: classes.customBadge }}
                                >
                                    <Notifications />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Popover
                            id={idPopOver}
                            open={openPopOver}
                            anchorEl={anchorElPopOver}
                            onClose={handleClosePopOver}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            PaperProps={{
                                style: notificationsCount === 0 ? emptyAlertStyles : alertStyles,
                            }}
                        >
                            {notificationsCount > 0 ? (
                                <AlertPopover
                                    alerts={notifications}
                                    handleReadNotifications={handleReadNotification}
                                    handlePanelItemClick={handleCommentsPanelItemClick}
                                    notificationType="notification"
                                />
                            ) : (
                                <Typography className={classes.typography}>
                                    {t("topbar.noNotifications")}
                                </Typography>
                            )}

                            <Grid container justify="center">
                                <Grid item>
                                    <Button
                                        style={{ marginBottom: "15px", marginTop: "20px" }}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            togglePopupNotifications();
                                            getNotificationsHistoryList();
                                            handleClosePopOver();
                                        }}
                                    >
                                        {t("topbar.showNotificationsHistory")}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Popover>
                        {popUpNotifications && (
                            <HistoricalAdvertises
                                open={popUpNotifications}
                                close={togglePopupNotifications}
                                information={notificationsHistoryList}
                                type="notifications"
                                setAdvertise={setNotifications}
                                setAdvertiseCount={setNotificationsCount}
                            />
                        )}

                        <Tooltip title={`${t("topbar.profile")}`}>
                            <IconButton onClick={handleClickProfile} className={classes.menuButton}>
                                {userProfilePhoto ? (
                                    <img
                                        alt="fotodeperfil"
                                        src={userProfilePhoto}
                                        style={{
                                            width: "30px",
                                            height: "30px",
                                            borderRadius: "50%",
                                        }}
                                    />
                                ) : (
                                    <AccountCircle />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorProfile}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorProfile)}
                            onClose={handleCloseProfile}
                        >
                            {/* TODO User profile */}
                            {/* <MenuItem onClick={() => handleMenuItemClick(BDTRoutes)}>
                                <Person />{" "}
                                <span className={classes.userMenuItemIcon}>{t("profile")}</span>
                            </MenuItem> */}
                            <MenuItem>
                                <span className={classes.userMenuItemIcon}>
                                    {profile?.name ? profile.name : t("login.unknownUser")}
                                </span>
                            </MenuItem>
                            {isAdmin && (
                                <MenuItem
                                    onClick={() => {
                                        handleCloseProfile();
                                        setTimeout(() => setUserSettings(true), 0);
                                    }}
                                >
                                    <Icons.Settings />
                                    <span className={classes.userMenuItemIcon}>Configuracion</span>
                                </MenuItem>
                            )}

                            <MenuItem onClick={handleLogOut}>
                                <PowerSettingsNew />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.logout")}
                                </span>
                            </MenuItem>
                        </Menu>
                    </div>

                    {userSettings && (
                        <UserSettingsModal
                            open={userSettings}
                            onOpenChange={() => setUserSettings(false)}
                        />
                    )}
                </Toolbar>
            </AppBar>

            <Snackbar
                open={snackbarAlarms.state}
                autoHideDuration={9000}
                onClose={handleCloseSnackbarAlarms}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={handleCloseSnackbarAlarms}
                    severity="warning"
                    style={{ backgroundColor: "#FFF633", color: "black", fontWeight: "bold" }}
                    action={
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={(ev) => {
                                handleSignalsPanelItemClick(
                                    BDTRoutes.Project.Viewer.Viewer.Builder(project.id),
                                    snackbarAlarms.signalId
                                );
                                handleCloseSnackbarAlarms(ev);
                            }}
                        >
                            {React.createElement(Icons.LastPage)}
                        </IconButton>
                    }
                >
                    <Typography variant="body2">{snackbarAlarms.description}</Typography>
                    <Typography variant="body2">{snackbarAlarms.date}</Typography>
                </Alert>
            </Snackbar>
        </div>
    );
};

const TopBarM = React.memo(TopBar);

export default TopBarM;
